/**
 * Row for a VSC report.
 */
import * as React from 'react';
import { Calendar, UserCheck } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { getRowPermission } from '@/hooks/usePageActions';
import type { VscReportPageActions } from './vsc-report-configs';
import {
  deriveVscReportDisplay,
  getVscReportActionState,
  resolveVscReportPermissions,
  type DerivedVscReportDisplay,
} from './vsc-report-table-utils';
import type { VscReport } from '@/types/vsc-report.types';

import { DataTableRowCheckbox } from '@/components/common/data-table/DataTableRowCheckbox';

/** Props for {@link VscReportRow}. */
interface VscReportRowProps {
  vscReport: VscReport;
  onView?: (vscReportId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onValidate?: () => void;
  pageActions?: VscReportPageActions;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

/** Bulk-select checkbox cell (only when the table shows selection). */
function SelectCell({
  isSelected,
  onToggleSelect,
}: Readonly<{
  isSelected: boolean;
  onToggleSelect?: () => void;
}>) {
  return (
    <td className="w-10 text-center">
      <div className="flex items-center justify-center">
        <DataTableRowCheckbox checked={isSelected} onCheckedChange={() => onToggleSelect?.()} />
      </div>
    </td>
  );
}

/** Session date plus optional establishment subtitle. */
function SessionCell({
  sessionDisplayText,
  establishmentName,
}: Readonly<{
  sessionDisplayText: string;
  establishmentName: string | undefined;
}>) {
  return (
    <td>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="font-medium text-foreground">{sessionDisplayText}</span>
        </div>
        {establishmentName && (
          <div className="ml-10">
            <span className="text-sm text-muted-foreground">{establishmentName}</span>
          </div>
        )}
      </div>
    </td>
  );
}

/** Volunteer display name with the VSC icon box. */
function VolunteerCell({ volunteerName }: Readonly<{ volunteerName: string }>) {
  return (
    <td>
      <div className="flex items-center gap-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
          <UserCheck className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
        </div>
        <span className="text-foreground">{volunteerName}</span>
      </div>
    </td>
  );
}

/** Status badge from {@link deriveVscReportDisplay}. */
function StatusCell({ display }: Readonly<{ display: DerivedVscReportDisplay }>) {
  return (
    <td>
      <LabelBadge
        label={display.statusConfig.label}
        icon={display.StatusIcon}
        variant={display.statusConfig.badgeVariant}
        color={display.statusConfig.badgeColor}
        size="md"
      />
    </td>
  );
}

/** Submitted-at date (`—` while still `to_fill`). */
function EntryDateCell({ entryDateText }: Readonly<{ entryDateText: string }>) {
  return (
    <td>
      <span className="text-sm text-foreground">{entryDateText}</span>
    </td>
  );
}

/** View / edit / validate / delete; edit and validate honor status-based disable. */
function ActionsCell({
  vscReportId,
  canEdit,
  canValidate,
  canDelete,
  editDisabled,
  editTooltip,
  validateDisabled,
  validateTooltip,
  onView,
  onEdit,
  onDelete,
  onValidate,
}: Readonly<{
  vscReportId: string;
  canEdit: boolean;
  canValidate: boolean;
  canDelete: boolean;
  editDisabled: boolean;
  editTooltip: string;
  validateDisabled: boolean;
  validateTooltip: string;
  onView?: (vscReportId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onValidate?: () => void;
}>) {
  return (
    <td className="text-center">
      <div className="flex items-center justify-center gap-1">
        <Button preset="row-view" onClick={() => onView?.(vscReportId)} />
        {canEdit && (
          <Button
            preset="row-edit"
            disabled={editDisabled}
            tooltipText={editTooltip}
            onClick={() => onEdit?.()}
          />
        )}
        {canValidate && (
          <Button
            preset="row-validate"
            disabled={validateDisabled}
            tooltipText={validateTooltip}
            onClick={() => onValidate?.()}
          />
        )}
        {canDelete && <Button preset="row-delete" onClick={() => onDelete?.()} />}
      </div>
    </td>
  );
}

/** Row for a VSC report. */
export const VscReportRow: React.FC<VscReportRowProps> = ({
  vscReport,
  onView,
  onEdit,
  onDelete,
  onValidate,
  pageActions,
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const { canEdit: canEditResolved, canValidate } = resolveVscReportPermissions(pageActions);
  const canEdit = getRowPermission(vscReport, 'action-edit-row', canEditResolved);
  const canDelete = getRowPermission(
    vscReport,
    'action-delete-row',
    pageActions?.canDeleteRow ?? true,
  );

  const display = deriveVscReportDisplay(vscReport);
  const actionState = getVscReportActionState(vscReport);

  return (
    <TableRow>
      {isSelectable && <SelectCell isSelected={isSelected} onToggleSelect={onToggleSelect} />}
      <SessionCell
        sessionDisplayText={display.sessionDisplayText}
        establishmentName={display.establishmentName}
      />
      <VolunteerCell volunteerName={display.volunteerName} />
      <StatusCell display={display} />
      <EntryDateCell entryDateText={display.entryDateText} />
      <ActionsCell
        vscReportId={vscReport.id}
        canEdit={canEdit}
        canValidate={canValidate}
        canDelete={canDelete}
        editDisabled={actionState.editDisabled}
        editTooltip={actionState.editTooltip}
        validateDisabled={actionState.validateDisabled}
        validateTooltip={actionState.validateTooltip}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
        onValidate={onValidate}
      />
    </TableRow>
  );
};
